import { useEffect } from 'react';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import {
    endpoints,
    getSavedSearchesQueryParams,
    useGetSavedSearchesQuery,
    useGetSavedSearchResultsMutation
} from 'tradera/state/services/discover-api';

export const useGetSavedSearches = (
    loadingTriggerInView: boolean,
    selectedSearchIndex: number
) => {
    const { data: sortedSearchesData } = useGetSavedSearchesQuery(
        getSavedSearchesQueryParams(true),
        {
            skip: !loadingTriggerInView
        }
    );

    // This hook gets the data if it exists regardless of the skip flag used
    // like in useGetSavedSearchesQuery above.
    // We also don't want to trigger a fetch for this data in TouchWeb which
    // only uses data from the useGetSavedSearchesQuery hook.
    const { data: unsortedSearchesData } =
        endpoints.getSavedSearches.useQueryState(
            getSavedSearchesQueryParams(false)
        );

    const { savedSearches: unsortedSavedSearches } = unsortedSearchesData || {};
    const { savedSearches: sortedSavedSearches } = sortedSearchesData || {};

    // In TouchWeb, unsortedSavedSearches will always be undefined.
    // In NextWeb unsortedSavedSearches may have been loaded on server.
    const savedSearchesCount =
        unsortedSavedSearches?.length || sortedSavedSearches?.length || 0;

    const selectedSavedSearch = sortedSavedSearches?.[selectedSearchIndex];
    const { name: savedSearchName, id: savedSearchId } =
        selectedSavedSearch || {};
    const [getSearchResults, getSearchResultResponse] =
        useGetSavedSearchResultsMutation();

    useEffect(() => {
        if (typeof savedSearchId !== 'undefined') {
            getSearchResults({ id: savedSearchId });
        }
    }, [getSearchResults, savedSearchId]);

    const { items, itemsMatched: totalItems } =
        getSearchResultResponse.data?.response?.result ?? {};

    useEffect(() => {
        if (
            loadingTriggerInView &&
            !getSearchResultResponse.isLoading &&
            typeof totalItems !== 'undefined'
        ) {
            GtmService.trackAction(
                'StartPage',
                'savedSearches',
                'number of search results on load',
                totalItems
            );
        }
    }, [getSearchResultResponse.isLoading, loadingTriggerInView, totalItems]);

    return {
        itemsIsLoading:
            getSearchResultResponse.isUninitialized ||
            getSearchResultResponse.isLoading,
        itemsError: getSearchResultResponse.error,
        savedSearchesCount,
        savedSearches: sortedSavedSearches || [],
        savedSearchName,
        savedSearchId,
        items: items || [],
        itemCount: items?.length || 0,
        totalItems: Number(totalItems) || 0
    };
};
